import React, { useState, useEffect, useRef } from 'react';
import TypingIndicator from './TypingIndicator';

interface RotatingTextLoaderProps {
  messages: string[];
  timings?: number[]; // Custom time in ms for each message
  defaultInterval?: number; // Default time if specific timing not provided
  showDots?: boolean;
  className?: string;
}

const RotatingTextLoader: React.FC<RotatingTextLoaderProps> = ({
  messages,
  timings = [],
  defaultInterval = 7000, // Default 3 seconds
  showDots = true,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (messages.length <= 1) return;

    const advanceMessage = () => {
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Move to next message
      setCurrentIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % messages.length;

        // Get timing for the next message
        const nextTiming = timings[nextIndex] || defaultInterval;

        // Set timer for next transition
        timerRef.current = setTimeout(advanceMessage, nextTiming);

        return nextIndex;
      });
    };

    // Get timing for initial message
    const initialTiming = timings[currentIndex] || defaultInterval;

    // Start the timer
    timerRef.current = setTimeout(advanceMessage, initialTiming);

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [messages, timings, defaultInterval]);

  return (
    <span className={`inline-flex items-center ${className}`}>
      {messages[currentIndex]}
      {showDots && (
        <>
          &nbsp; <TypingIndicator />
        </>
      )}
    </span>
  );
};

export default RotatingTextLoader;
